import { Mail, MapPin, Instagram, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EMAIL, WHATSAPP_DISPLAY, INSTAGRAM, LOCATION, MAPS_URL, MAP_EMBED, NAV, MAIL_HREF, scrollToId, waLink } from "@/lib/site";
import { Reveal, Overline } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Logo } from "./Header";

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", value: WHATSAPP_DISPLAY, href: waLink(), icon: <WhatsAppIcon className="h-5 w-5" />, ext: true },
  { id: "email", label: "Email", value: EMAIL, href: MAIL_HREF, icon: <Mail className="h-5 w-5" /> },
  { id: "instagram", label: "Instagram", value: "@tsr_industry", href: INSTAGRAM, icon: <Instagram className="h-5 w-5" />, ext: true },
  { id: "location", label: "Location", value: LOCATION, href: MAPS_URL, icon: <MapPin className="h-5 w-5" />, ext: true },
];

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[var(--sand)]">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal>
        <Overline>Contact</Overline>
        <h2 className="font-display font-bold tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95]">
          Let's make<br /><span className="text-gold-grad">something exact.</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 border-t border-slate-400/60">
        {CHANNELS.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.06}>
            <a data-testid={`contact-${c.id}-link`} href={c.href} {...(c.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={`group flex items-center justify-between gap-4 py-8 border-b border-slate-400/60 ${i % 2 === 0 ? "sm:pr-8 sm:border-r" : "sm:pl-8"}`}>
              <span className="flex items-start gap-4 min-w-0">
                <span className="mt-1 grid place-items-center h-11 w-11 shrink-0 rounded-full bg-[#0e1116] text-[#e8c36a]">{c.icon}</span>
                <span className="min-w-0">
                  <span className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-600">{c.label}</span>
                  <span className="block font-display text-lg sm:text-2xl font-semibold mt-1 break-words">{c.value}</span>
                </span>
              </span>
              <ArrowUpRight className="h-6 w-6 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12">
        <div className="chamfer relative overflow-hidden border border-slate-300 bg-white">
          <iframe data-testid="contact-map" title="TSR Industry location on Google Maps" src={MAP_EMBED}
            className="block w-full h-[320px] sm:h-[420px] border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          <a data-testid="contact-directions-btn" href={MAPS_URL} target="_blank" rel="noopener noreferrer"
            className="absolute top-4 left-4 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[#0e1116] text-white font-semibold shadow-xl hover:bg-black transition-colors">
            <MapPin className="h-4 w-4 text-[#e8c36a]" /> Get directions
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0e1116] text-slate-300">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-12">
      <div className="md:col-span-5 flex items-start gap-4">
        <Logo className="h-20 w-20" />
        <div>
          <p className="font-display text-xl font-bold text-white">TSR Industry</p>
          <p className="mt-1 text-sm">CNC Wood & Iron Cutting · {LOCATION}</p>
        </div>
      </div>
      <nav className="md:col-span-4 grid grid-cols-2 gap-2 text-sm" aria-label="Footer">
        {NAV.map((n) => (
          <button key={n.id} data-testid={`footer-nav-${n.id}`} onClick={() => scrollToId(n.id)} className="text-left hover:text-[#e8c36a] transition-colors py-1">{n.label}</button>
        ))}
        <button data-testid="footer-nav-quote" onClick={() => scrollToId("quote")} className="text-left hover:text-[#e8c36a] transition-colors py-1">Get a Quote</button>
      </nav>
      <div className="md:col-span-3 text-sm space-y-2">
        <a data-testid="footer-email-link" href={MAIL_HREF} className="block hover:text-[#e8c36a] break-all">{EMAIL}</a>
        <a data-testid="footer-instagram-link" href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="block hover:text-[#e8c36a]">Instagram</a>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-400">
        <span>© {new Date().getFullYear()} TSR Industry. All rights reserved.</span>
        <Link to="/admin" data-testid="footer-admin-link" className="hover:text-slate-200">Admin</Link>
      </div>
    </div>
  </footer>
);
